import type { ContentType } from "~/models/Content/ContentType"
import type { BaseSearchResponse } from "~/models/Content/Response"
import type { AggregationsMap } from "~/models/Content/SearchResult"
import { formatSearchResults } from "~/mapping/formatSearchResults"
import { mapContents } from "~/mapping/mapToContentTypes"

export interface SearchResponse<T> {
  data: T[]
  count: number
  aggregations?: AggregationsMap
}

const MAX_DEPTH = 2

export function useSearch() {
  const api = useApiClients()

  const { ibexa } = useRuntimeConfig().public

  return async function fetchResults<T extends ContentType>(
    query: IbexaQuery<ContentType>,
    depth: number = 0,
  ): Promise<SearchResponse<MappedIbexaContent<T>>> {
    const response = await api.ibexa<BaseSearchResponse>("/search", {
      params: buildIbexaQuery(query),
    })

    const results = formatSearchResults(response, ibexa.baseUrl)

    const mappedResults = mapContents<T>(results)

    const relationsIds = mappedResults.flatMap(
      item => item.relationsIds,
    )

    if (!relationsIds.length || depth >= MAX_DEPTH) {
      return {
        data: mappedResults,
        count: results.count,
        aggregations: results.aggregations,
      }
    }

    try {
      const relations = await fetchResults<T>(
        { contentId: relationsIds },
        depth + 1,
      )

      return {
        data: mappedResults.map(it => ({
          ...it,
          relatedItems: relations.data.filter(relation =>
            it.relationsIds.includes(relation.contentId),
          ),
        })),
        count: results.count,
        aggregations: results.aggregations,
      }
    } catch (error) {
      captureException(error)
      return {
        data: mappedResults,
        count: results.count,
        aggregations: results.aggregations,
      }
    }
  }
}
